import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Select2OptionData} from '@components/shared/select2/select2.interface';
import {Company} from '@models/company';
import {DataViewColumn, DataViewRow} from '@models/dataview';
import {AlertService} from '@services/alert.service';
import {DataFrameService} from '@services/dataframes.service';
import {DATE_FORMAT, SERVER_DATE_FORMAT} from '@utils/constants';
import moment from 'moment';
import {Subscription} from "rxjs";

@Component({
    selector: 'app-annualize-period-form',
    templateUrl: './annualize-period-form.component.html',
    styleUrls: ['./annualize-period-form.component.scss'],
    standalone: false
})
export class AnnualizePeriodFormComponent implements OnInit {
    @Input() company: Company;
    @Input() columns: DataViewColumn[];
    @Input() rows: DataViewRow[];
    @Output() closeAnnualizePeriodForm = new EventEmitter<boolean>();

    readonly MAX_SCENARIO_NAME_LENGTH = 200;

    subsArr$: Subscription[] = [];
    companyId: Number;
    selectedReferenceFrameIndex: number = -1;
    scenarioName: string = '';
    scenarioType: string | null = null;
    isFormComplete = false;
    isSubmitting = false;
    isLoading = false;
    wasValidated = false;
    periodOptions: Select2OptionData[] = [];
    scenarioTypeOptions: Select2OptionData[] = [];
    hasReferenceFrameBeenChangedByUser = false;

    private readonly ALL_SCENARIO_TYPE_OPTIONS: Select2OptionData[] = [
        { id: 'PROJECTION', text: 'Projection' },
        { id: 'PROFORMA', text: 'Proforma' },
    ];

    constructor(
        private _dataFrameService: DataFrameService,
        private _alertService: AlertService
    ) {
    }

    ngOnInit(): void {
        this.columns.forEach((column, colIdx) => {
            if (column.monthsInInterval === 12){
                return;
            }
            const periodLabel = (column.statementDate + " - " + column.preparationType + " - " + column.reportingInterval + (column.projectionName ? " - " + column.projectionName : ""));
            this.periodOptions.push({
                id: String(colIdx),
                text: periodLabel,
            })
        })
    }

    updateSelectedReferenceFrameIndex(selectedReferenceFrameIndex: string) {
        this.selectedReferenceFrameIndex = Number(selectedReferenceFrameIndex);
        this.hasReferenceFrameBeenChangedByUser = true;
        this.refreshScenarioTypeOptions();
        this.setIsFormComplete();
    }

    updateScenarioName(scenarioName: string): void {
        this.scenarioName = scenarioName;
        this.setIsFormComplete();
    }

    updateScenarioType(scenarioType: string): void {
        this.scenarioType = scenarioType;
        this.setIsFormComplete();
    }

    private refreshScenarioTypeOptions(): void {
        const sourceScenario = this.columns[this.selectedReferenceFrameIndex]?.scenario;
        if (sourceScenario === 'PROJECTION') {
            this.scenarioTypeOptions = [{ id: 'PROJECTION', text: 'Projection' }];
            this.scenarioType = 'PROJECTION';
        } else {
            this.scenarioTypeOptions = this.ALL_SCENARIO_TYPE_OPTIONS;
            this.scenarioType = 'PROFORMA';
        }
    }

    setIsFormComplete(): void {
        this.isFormComplete = this.selectedReferenceFrameIndex !== -1
            && this.isScenarioNameValid()
            && !!this.scenarioType
            && !this.isDuplicateAnnualizedPeriod();
    }

    private isScenarioNameValid(): boolean {
        return !!this.scenarioName
            && !!this.scenarioName.trim()
            && this.scenarioName.length <= this.MAX_SCENARIO_NAME_LENGTH;
    }

    /**
     * Returns true when an annualized period already exists for the reference period's
     * statement date with the same scenario type and scenario name. Used to prevent
     * creating duplicates.
     */
    isDuplicateAnnualizedPeriod(): boolean {
        if (this.selectedReferenceFrameIndex === -1) { return false; }
        if (!this.scenarioType) { return false; }
        if (!this.isScenarioNameValid()) { return false; }

        const referenceColumn = this.columns[this.selectedReferenceFrameIndex];
        if (!referenceColumn) { return false; }
        const trimmedScenarioName = this.scenarioName.trim();

        return this.columns.some(column =>
            column.statementDate === referenceColumn.statementDate
            && column.monthsInInterval === 12
            && column.scenario === this.scenarioType
            && column.projectionName === trimmedScenarioName
        );
    }

    isFieldInvalid(fieldName: string): boolean {
        if ((fieldName === 'scenarioType' || fieldName === 'scenarioName')
            && this.isDuplicateAnnualizedPeriod()) {
            return true;
        }

        if (!this.wasValidated) {
            return false;
        }

        switch(fieldName) {
            case 'referencePeriod':
                return this.selectedReferenceFrameIndex === -1;
            case 'scenarioName':
                return !this.isScenarioNameValid();
            case 'scenarioType':
                return !this.scenarioType;
            default:
                return false;
        }
    }

    getScenarioNameError(): string | null {
        if (!this.wasValidated) { return null; }
        if (!this.scenarioName || !this.scenarioName.trim()) {
            return 'Please enter a Scenario Name.';
        }
        if (this.scenarioName.length > this.MAX_SCENARIO_NAME_LENGTH) {
            return 'Scenario name cannot exceed 200 characters.';
        }
        return null;
    }

    validateForm(): boolean {
        this.wasValidated = true;
        return this.selectedReferenceFrameIndex !== -1
            && this.isScenarioNameValid()
            && !!this.scenarioType
            && !this.isDuplicateAnnualizedPeriod();
    }

    triggerCloseGeneratePeriods(newStatementCreated = false) {
        this.closeAnnualizePeriodForm.emit(newStatementCreated);
    }

    handleGenerateAnnualizedPeriod() {
        // Validate form before submission
        if (!this.validateForm()) {
            return;
        }

        this.isSubmitting = true;
        this._dataFrameService.generateAnnualizedPeriod(
            this.company.id,
            this.scenarioName.trim(),
            this.scenarioType,
            this.colToFrameKey(this.columns[this.selectedReferenceFrameIndex]),
        ).subscribe((data) => {
            this._alertService.success('Statement Built! If the new statement isn\'t shown, please reload the page.');
            this.triggerCloseGeneratePeriods(true);
            this.isSubmitting = false;
        }, (err) => {
            this._alertService.error(`${err.message}`);
            this.isSubmitting = false;
        })
    }

    colToFrameKey(column: DataViewColumn) {
        return {
            scenario: column.scenario,
            statement_date: moment(column.statementDate, DATE_FORMAT).format(SERVER_DATE_FORMAT),
            reporting_interval: column.reportingInterval,
            preparation_type: column.preparationType,
            projection_name: column.projectionName,
            period_key_uuid: column.periodKeyUuid
        }
    }

}
